import { NavBar } from '@/features/navigation/nav-bar'
import { cn } from '@/shared/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef } from 'react'

const layoutVariants = cva(cn('flex flex-col'), {
  variants: {
    height: {
      'full-height': 'min-h-screen-stable',
      'content-height': 'h-auto',
    },
    safeY: {
      safe: 'pt-safe-area pb-safe-area',
      'no-safe': '',
    },
    safeX: {
      'no-safe': '',
      safe: 'pr-safe-area pl-safe-area',
    },
    nav: {
      nav: 'pb-nav-safe',
      'no-nav': '',
    },
    width: {
      full: '',
      appContainer: 'app-container',
    },
  },
  compoundVariants: [
    {
      safeY: 'safe',
      nav: 'nav',
      className: 'pb-safe-with-nav',
    },
  ],
  defaultVariants: {
    height: 'full-height',
    safeX: 'safe',
    safeY: 'safe',
    nav: 'no-nav',
    width: 'appContainer',
  } as const,
})

export type LayoutProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof layoutVariants>

/**
 * A flexible layout component with safe area paddings and optional bottom navigation.
 *
 * @param {object} props - The props for the Layout component.
 * @param {'full-height' | 'content-height'} [props.height='full-height'] - Determines the height of the layout
 *   container.
 *    - 'full-height' — Fills the entire screen height (min-h-screen-stable).
 *    - 'content-height' — Auto height based on content.
 * @param {'safe' | 'no-safe'} [props.safeY='safe'] - Controls vertical safe area paddings.
 *    - 'safe' — Adds top (pt-safe-area) and bottom (pb-safe-area) padding.
 *    - 'no-safe' — Disables vertical safe paddings.
 * @param {'safe' | 'no-safe'} [props.safeX='safe'] - Controls horizontal safe area paddings.
 *    - 'safe' — Adds left (pl-safe-area) and right (pr-safe-area) padding.
 *    - 'no-safe' — Disables horizontal safe paddings.
 * @param {'nav' | 'no-nav'} [props.nav='no-nav'] - Controls space for bottom navigation.
 *    - 'nav' — Adds padding for bottom navigation (pb-nav-safe).
 *    - 'no-nav' — No padding for nav.
 * @param {'full' | 'appContainer'} [props.width='appContainer'] - Controls the maximum width of the layout container.
 *    - 'full' — Full width of the viewport.
 *    - 'appContainer' — Applies a fixed-width container class (app-container) for content alignment.
 * @param {string} [props.className] - Additional CSS class names.
 * @param {React.ReactNode} props.children - Child nodes to render inside the layout.
 * @param {React.HTMLAttributes<HTMLDivElement>} [props.props] - Additional div props.
 * @returns {JSX.Element} The rendered Layout component.
 */
const Layout = forwardRef<HTMLDivElement, LayoutProps>(
  (
    { className, height, safeX, safeY, nav, width, children, ...props },
    ref,
  ) => {
    return (
      <div
        className={cn(
          layoutVariants({ height, safeX, safeY, width, nav }),
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
        {nav === 'nav' && <NavBar />}
      </div>
    )
  },
)
Layout.displayName = 'Layout'

export { Layout }
